import { useCallback, useEffect, useState } from "react";
import {
  type ContactInfo,
  ContactInfoType,
  type FirmPreferences,
  type UserInvite,
  type UserType,
} from "@/briefpoint-client";
import type { UserResponse } from "@/lib/types";
import { useFirmApi } from "./useApi";

type UseFirmType = [
  UserResponse[] | null,
  UserInvite[] | null,
  () => Promise<void>,
  (email: string, type: UserType) => Promise<void>,
  (userId: string, activate: boolean) => Promise<void>,
  (preferences: FirmPreferences) => Promise<void>,
  (inviteId: string) => Promise<void>,
];
export default function useUsers(
  firmId: string | undefined,
  loadInvites = true,
): UseFirmType {
  const [users, setUsers] = useState<UserResponse[] | null>(null);
  const [invites, setInvites] = useState<UserInvite[] | null>(null);
  const firmApi = useFirmApi();

  const loadUsers = useCallback(async () => {
    if (firmId) {
      const usersCall = (await firmApi.firmGetUsers({
        id: firmId,
      })) as UserResponse[];
      const invitesCall = loadInvites
        ? ((await firmApi.firmGetInvites({ id: firmId })) as UserInvite[])
        : [];

      const results = { users: usersCall, invites: invitesCall };

      setUsers(results.users);
      if (loadInvites) {
        setInvites(results.invites);
      }
    }
  }, [firmApi, firmId, loadInvites]);

  const sendInvite = useCallback(
    async (email: string, type: UserType) => {
      if (!firmId) return;

      if (
        users?.find((u) =>
          u.contactInfo?.find(
            (x: ContactInfo) =>
              x.type === ContactInfoType.Email &&
              x.value?.toLowerCase() === email.toLowerCase(),
          ),
        )
      ) {
        return;
      }
      await firmApi.firmInviteUser({
        id: firmId,
        inviteUserToFirm: { emailAddress: email, userType: type },
      });
      setInvites(await firmApi.firmGetInvites({ id: firmId }));
    },
    [firmApi, firmId, users],
  );

  const activateDeactivateUser = useCallback(
    async (userId: string, activate: boolean) => {
      if (!firmId) {
        return;
      }
      if (activate) {
        await firmApi.firmActivateUser({ id: firmId, userId });
      } else {
        await firmApi.firmDeactivateUser({ id: firmId, userId });
      }
      loadUsers();
    },
    [firmApi, firmId, loadUsers],
  );

  const updatePreferences = useCallback(
    async (preferences: FirmPreferences) => {
      if (!firmId) return;

      await firmApi.firmUpdatePreferences({
        id: firmId,
        firmPreferences: preferences,
      });
    },
    [firmApi, firmId],
  );

  const cancelInvite = useCallback(
    async (inviteId: string) => {
      if (!firmId) {
        return;
      }
      await firmApi.firmCancelInvite({ firmId, id: inviteId });
      setInvites(await firmApi.firmGetInvites({ id: firmId }));
    },
    [firmApi, firmId],
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return [
    users,
    invites,
    loadUsers,
    sendInvite,
    activateDeactivateUser,
    updatePreferences,
    cancelInvite,
  ];
}
